/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 26/08/2025 - 13:49:05
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 26/08/2025
    * - Author          : 
    * - Modification    : 
**/


import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
  Accordion,
  Image,
  Form,
  OverlayTrigger,
  Button,
  Tooltip,
} from "react-bootstrap";
import {
  addVerticalPolygon,
  addVerticalSlopeArea,
  deleteVerticalHeights,
  removeVerticalPolygon,
  setVerticalLowerHeight,
  setVerticalUpperHeight,
  CalculateGlare,
  setVerticalAzimutyhValue,
  deleteAzimuthValue,
  setVeticalAverageElevation,
  removeVerticalAvgElevation,
  setVerticalAzimuthCheck,
  setVerticalElevationCheck,
  deleteVerticalchecked,
  setVerticalSwap,
  deleteVerticalSwap,
} from "../../../../store/reducers/bmapSlice";
import { useParams } from 'react-router-dom';
import { apis } from "../../../../api/data";
import { batchApis } from "../../../../api/batchApis";
import AddDetectionPoint from "../../../../images/Add Facade PV Area Vector.png";
// import icons
import { IoInformationCircleOutline } from "react-icons/io5";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { toast } from "react-toastify";
import { putData } from "../../../../utils/axios";
import IconWrapper from "../../../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';

// Import Firebase
import { doc, getDoc, setDoc, updateDoc, deleteField, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase';

// Define the azimuth response interface
interface AzimuthResponse {
  azimuth: number;
}

// Define the elevation data interface
interface ElevationData {
  upperEdge: {
    elevation: any;
    lat: number;
    lon: number;
    offset: number;
    tag: string;
  } | null;
  lowerEdge: {
    elevation: any;
    lat: number;
    lon: number;
    offset: number;
    tag: string;
  } | null;
}

// Define the VerticalElevationData interface
interface VerticalElevationData {
  isAutoCalculated: boolean;
  manualValue: number | null;
  apiValue: number | null;
}

// Define the consolidated data structure for vertical PV areas
interface ConsolidatedVerticalPVArea {
  // Polygon coordinates
  polygon?: Array<{lat: number, lng: number}>;
  
  // Azimuth data
  azimuth?: {
    value: number | null;
    isAutoCalculated: boolean;
  };
  
  // Elevation data
  elevation?: {
    value: number | null;
    isAutoCalculated: boolean;
    apiValue: number | null;
    manualValue: number | null;
  };
  
  // Heights
  heights?: {
    upper?: number | null;
    lower?: number | null;
  };
  
  // Slope angle
  slope?: number | null;
  
  // Whether the upper and lower edges are swapped
  swapped?: boolean;
  
  // Point elevations data
  elevationData?: ElevationData | null;
  
  // Add an index signature to allow for additional properties
  [key: string]: any;
}

// Firebase helper functions for consolidated data structure
const saveVerticalPVArea = async (
  projectId: string,
  areaIndex: number,
  areaData: Partial<ConsolidatedVerticalPVArea>
): Promise<void> => {
  try {

    if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
      console.error('Invalid projectId in saveVerticalPVArea:', projectId);
      return;
    }

    const projectRef = doc(db, 'projects', projectId);
    
    // Create the area ID based on index
    const areaId = `VPV-${areaIndex + 1}`;
    // console.log(`Saving data for ${areaId} with:`, JSON.stringify(areaData));
    
    // Get existing data to merge with new data
    const projectDoc = await getDoc(projectRef);
    const existingData = projectDoc.exists() ? 
      ((projectDoc.data().verticalPVAreas || {})[areaId] || {}) : {};
    
    // Create a clean copy of the merged data (but don't merge heights yet)
    let mergedData = {
      ...existingData
    };
    
    // Copy non-height properties from areaData
    Object.keys(areaData).forEach(key => {
      if (key !== 'heights') {
        mergedData[key] = areaData[key];
      }
    });
    
    // Special handling for heights
    if (areaData.heights) {
      // Create a new heights object starting with existing values
      const finalHeights: { 
        upper: number | null; 
        lower: number | null; 
      } = { 
        upper: null, 
        lower: null 
      };
      
      // First, copy existing heights as baseline
      if (existingData.heights) {
        if (existingData.heights.upper !== undefined) {
          finalHeights.upper = existingData.heights.upper;
        }
        if (existingData.heights.lower !== undefined) {
          finalHeights.lower = existingData.heights.lower;
        }
      }
      
      // Then, apply new height values if provided
      if (areaData.heights.upper !== undefined) {
        finalHeights.upper = areaData.heights.upper;
      }
      if (areaData.heights.lower !== undefined) {
        finalHeights.lower = areaData.heights.lower;
      }
      
      // Ensure values are numbers or null
      if (finalHeights.upper !== null && typeof finalHeights.upper === 'string') {
        finalHeights.upper = parseFloat(finalHeights.upper);
      }
      if (finalHeights.lower !== null && typeof finalHeights.lower === 'string') {
        finalHeights.lower = parseFloat(finalHeights.lower);
      }
      
      // Add heights to merged data
      mergedData.heights = finalHeights;
      
      // console.log(`Final heights for ${areaId}:`, mergedData.heights);
    }
    
    // Update Firebase with the consolidated data
    await updateDoc(projectRef, {
      [`verticalPVAreas.${areaId}`]: mergedData
    });
    
    // console.log(`Successfully saved data for ${areaId}`);
  } catch (error) {
    console.error(`Error saving vertical PV area:`, error);
  }
};


// Load all vertical PV areas
const loadVerticalPVAreas = async (projectId: string): Promise<{[areaId: string]: ConsolidatedVerticalPVArea}> => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    
    if (projectDoc.exists()) {
      const data = projectDoc.data();
      return data.verticalPVAreas || {};
    }
    
    return {};
  } catch (error) {
    console.error("Error loading vertical PV areas:", error);
    return {};
  }
};


// Delete a vertical PV area

const deleteVerticalPVArea = async (
  projectId: string,
  areaIndex: number
): Promise<void> => {
  try {
    // console.log(`Starting deletion of vertical PV area at index ${areaIndex}`);
    const projectRef = doc(db, 'projects', projectId);
    
    // Get current data
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      // console.log("Project document does not exist");
      return;
    }
    
    const data = projectDoc.data();
    // Use a separate variable name to be clear this is the Firebase data
    const allAreas = data.verticalPVAreas || {};
    
    // console.log("Current areas in Firebase:", Object.keys(allAreas));
    
    // Compute the Firebase ID for the area to delete (convert from 0-based to 1-based)
    const areaIdToDelete = `VPV-${areaIndex + 1}`;
    // console.log(`Area ID to delete: ${areaIdToDelete}`);
    
    // Check if this area exists in Firebase
    if (!allAreas[areaIdToDelete]) {
      // console.log(`Area ${areaIdToDelete} not found in Firebase, cannot delete`);
      return;
    }
    
    // Simple check - if this is the only area, just clear everything
    if (Object.keys(allAreas).length === 1) {
      // console.log("Only one area left, removing entire verticalPVAreas field");
      await updateDoc(projectRef, {
        verticalPVAreas: deleteField()
      });
      return;
    }
    
    // IMPORTANT: Two-step deletion process
    // 1. First, create a new object WITHOUT the area to delete
    const remainingAreas: Record<string, any> = {};
    Object.entries(allAreas).forEach(([id, value]) => {
      if (id !== areaIdToDelete) {
        remainingAreas[id] = value;
      }
    });
    
    // console.log(`After removing ${areaIdToDelete}, remaining areas:`, Object.keys(remainingAreas));
    
    // 2. Then, completely clear verticalPVAreas and set the new value
    // This ensures we don't have any stale data
    await updateDoc(projectRef, {
      verticalPVAreas: deleteField()
    });
    
    // 3. Now add back all remaining areas
    await updateDoc(projectRef, {
      verticalPVAreas: remainingAreas
    });
    
    // console.log("Deletion completed successfully");
  } catch (error) {
    console.error(`Error deleting vertical PV area:`, error);
    toast.error("Error deleting vertical PV area");
  }
};


// Migration function to convert old data structure to new format
const migrateToConsolidatedVerticalStructure = async (projectId: string): Promise<void> => {
  try {
    // Get the project document
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    
    if (!projectDoc.exists()) {
      // console.log("Project does not exist, nothing to migrate");
      return;
    }
    
    const data = projectDoc.data();
    
    // Check if migration is needed
    if (data.verticalPVAreas) {
      // console.log("Data already in consolidated format, no migration needed");
      return;
    }
    
    // Get the old data structure elements
    const verticalPolygons = data.verticalPolygons || [];
    const verticalAzimuthValues = data.verticalAzimuthValue || [];
    const verticalAzimuthChecked = data.verticalAzimuthChecked || [];
    const verticalElevationChecked = data.verticalElevationChecked || [];
    const verticalAverageElevation = data.verticalAverageElevation || [];
    const verticalLowerHeights = data.verticalLowerHeight || [];
    const verticalUpperHeights = data.verticalUpperHeight || [];
    const verticalSwapValues = data.verticalSwap || [];
    const verticalSlopeAreas = data.verticalSlopeArea || [];
    const verticalPolygonAzimuthData = data.verticalPolygonAzimuthData || [];
    const verticalPolygonElevation = data.verticalPolygonElevation || {};
    
    // Create consolidated structure
    const verticalPVAreas: Record<string, ConsolidatedVerticalPVArea> = {};
    
    // Number of areas to process
    const numAreas = Math.max(
      verticalPolygons.length,
      verticalAzimuthValues.length,
      verticalAverageElevation.length,
      verticalLowerHeights.length,
      verticalUpperHeights.length
    );
    
    // Process each area
    for (let i = 0; i < numAreas; i++) {
      const areaId = `VPV-${i + 1}`;
      
      // Parse values safely
      let azimuthValue: number | null = null;
      if (verticalAzimuthValues[i] !== undefined) {
        const parsedValue = typeof verticalAzimuthValues[i] === 'string' ? 
          parseFloat(verticalAzimuthValues[i]) : verticalAzimuthValues[i];
        azimuthValue = isNaN(parsedValue) ? null : parsedValue;
      }
      
      let elevationValue: number | null = null;
      if (verticalAverageElevation[i] !== undefined) {
        const parsedValue = typeof verticalAverageElevation[i] === 'string' ? 
          parseFloat(verticalAverageElevation[i]) : verticalAverageElevation[i];
        elevationValue = isNaN(parsedValue) ? null : parsedValue;
      }
      
      let upperHeight: number | null = null;
      if (verticalUpperHeights[i] !== undefined) {
        const parsedValue = typeof verticalUpperHeights[i] === 'string' ? 
          parseFloat(verticalUpperHeights[i]) : verticalUpperHeights[i];
        upperHeight = isNaN(parsedValue) ? null : parsedValue;
      }
      
      let lowerHeight: number | null = null;
      if (verticalLowerHeights[i] !== undefined) {
        const parsedValue = typeof verticalLowerHeights[i] === 'string' ? 
          parseFloat(verticalLowerHeights[i]) : verticalLowerHeights[i];
        lowerHeight = isNaN(parsedValue) ? null : parsedValue;
      }
      
      let slopeValue: number | null = null;
      if (verticalSlopeAreas[i] !== undefined) {
        const parsedValue = typeof verticalSlopeAreas[i] === 'string' ? 
          parseFloat(verticalSlopeAreas[i]) : verticalSlopeAreas[i];
        slopeValue = isNaN(parsedValue) ? null : parsedValue;
      }
      
      // Get any existing elevation data
      let elevationDataObj: ElevationData | null = null;
      if (verticalPolygonAzimuthData[i] && verticalPolygonAzimuthData[i].polygonData) {
        elevationDataObj = verticalPolygonAzimuthData[i].polygonData;
      }
      
      const areaData: ConsolidatedVerticalPVArea = {
        polygon: verticalPolygons[i] || [],
        azimuth: {
          value: azimuthValue,
          isAutoCalculated: Boolean(verticalAzimuthChecked[i])
        },
        elevation: {
          value: elevationValue,
          isAutoCalculated: Boolean(verticalElevationChecked[i] ?? true),
          apiValue: verticalPolygonElevation[i]?.apiValue ?? null,
          manualValue: verticalPolygonElevation[i]?.manualValue ?? null
        },
        heights: {
          upper: upperHeight,
          lower: lowerHeight
        },
        slope: slopeValue,
        swapped: Boolean(verticalSwapValues[i]),
        elevationData: elevationDataObj
      };
      
      verticalPVAreas[areaId] = areaData;
    }
    
    // Save the consolidated structure
    await updateDoc(projectRef, { 
      verticalPVAreas: verticalPVAreas as Record<string, unknown>
    });
    
    // console.log(`Migrated ${numAreas} vertical PV areas to consolidated structure`);
  } catch (error) {
    console.error("Error migrating to consolidated structure:", error);
  }
};

const VerticalPvAreaPage: React.FC<{ projectName: string }> = ({
  projectName,
}) => {
  const { projectId } = useParams<{ projectId: string | any }>();
  
  const polygons = useSelector(
    (state: RootState) => state.bmap.verticalPolygons
  );
  const verticalAzimuth = useSelector(
    (state: RootState) => state.bmap.verticalAzimuthValue
  );
  const vertivalSlopeArea = useSelector(
    (state: RootState) => state.bmap.verticalSlopeArea
  );
  const verticalLowerHeight = useSelector(
    (state: RootState) => state.bmap.verticalLowerHeight
  );
  const verticalUpperHeight = useSelector(
    (state: RootState) => state.bmap.verticalUpperHeight
  );
  const selectedArea = useSelector(
    (state: RootState) => state.bmap.selectedArea
  );
  const averageElevation = useSelector(
    (state: RootState) => state.bmap.verticalAverageElevation
  );
  const checkedAzimuth = useSelector(
    (state: RootState) => state.bmap.verticalAzimuthChecked
  );
  const checkedAvgElevation = useSelector(
    (state: RootState) => state.bmap.verticalElevationChecked
  );
  const verticalSwap = useSelector(
    (state: RootState) => state.bmap.verticalSwap
  );
  
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const dispatch = useDispatch();
  const [isDropdownExpanded, setIsDropdownExpanded] = useState<boolean[]>([]);
  const [elevationData, setElevationData] = useState<ElevationData[]>([]);
  const [polygonLength, setPolygonLength] = useState<number>(0);
  const [widthValues, setWidthValues] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [verticalElevationData, setVerticalElevationData] = useState<Record<number, VerticalElevationData>>({});
  const [azimuthDebounceTimers, setAzimuthDebounceTimers] = useState<{[key: number]: NodeJS.Timeout | null}>({});
  // Track which polygons have completed elevation fetching
  const [elevationFetchStatus, setElevationFetchStatus] = useState<{[index: number]: 'pending' | 'fetching' | 'complete' | 'error'}>({});
  const [calculationScheduled, setCalculationScheduled] = useState<{[index: number]: boolean}>({});
  const [azimuthRequestIds, setAzimuthRequestIds] = useState<{[index: number]: number}>({});
  const pendingElevationUpdatesRef = useRef<{[index: number]: boolean}>({});
  const [calculatingAzimuth, setCalculatingAzimuth] = useState<{[index: number]: boolean}>({});
  const { t } = useTranslation(['verticalPvArea', 'common']);
  const [validationErrors, setValidationErrors] = useState<{[index: number]: {upper?: boolean, lower?: boolean}}>({});
  // Track previous polygon coordinates to only fetch when dragging ends
  const prevPolygonsRef = useRef<typeof polygons>([]); 
  const isDraggingRef = useRef<boolean>(false);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const safeProjectId = () => {
    if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
      console.error('Invalid projectId detected:', projectId);
      return false;
    }
    return projectId;
  };

  // Function to refetch polygon elevation (used after drag operations)
  const refetchPolygonElevation = (index: number, isPostDrag = false) => {
    // If already dragging, don't do anything
    if (isDraggingRef.current) {
      return;
    }
    
    // Use setTimeout to ensure we don't call the API too frequently
    setTimeout(async () => {
      try {
        // Mark as fetching
        setElevationFetchStatus(prev => ({
          ...prev,
          [index]: 'fetching'
        }));
        
        // Fetch the elevation data
        await fetchElevations(index);
        
        // If this is a post-drag update and auto-calculate is on, trigger azimuth calculation
        if (isPostDrag && checkedAzimuth[index]) {
          setTimeout(() => {
            forceAzimuthCalculation(index);
          }, 500); // Delay to ensure elevation data is processed
        }
      } catch (error) {
        console.error(`Error fetching elevation for polygon ${index}:`, error);
        setElevationFetchStatus(prev => ({
          ...prev,
          [index]: 'error'
        }));
      }
    }, 300);
  };

  // Function to handle the elevation auto-calculate change
  const handleElevationAutoCalculateChange = async (index: number, isChecked: boolean) => {
    if (!projectId) return;
    
    try {
      // Update Redux state first
      dispatch(
        setVerticalElevationCheck({
          index,
          check: isChecked,
        })
      );
      
      // Get current elevation data
      const currentData = verticalElevationData[index] || {
        isAutoCalculated: false,
        manualValue: null,
        apiValue: null
      };
      
      // If turning ON auto-calculate, we want to use the API value
      // If turning OFF auto-calculate, use the API value as initial manual value if no manual value exists
      let updatedManualValue = currentData.manualValue;
      if (!isChecked && currentData.apiValue !== null && currentData.manualValue === null) {
        updatedManualValue = currentData.apiValue;
      }
      
      // Create updated data with the correct isAutoCalculated flag
      const updatedData: VerticalElevationData = {
        ...currentData,
        isAutoCalculated: isChecked,
        manualValue: updatedManualValue
      };
      
      // Update local state
      setVerticalElevationData(prev => ({
        ...prev,
        [index]: updatedData
      }));
      
      // Save to consolidated data in Firebase
      await saveVerticalPVArea(projectId, index, {
        elevation: {
          value: isChecked && updatedData.apiValue !== null 
            ? updatedData.apiValue 
            : (!isChecked && updatedManualValue !== null ? updatedManualValue : null),
          isAutoCalculated: isChecked,
          apiValue: updatedData.apiValue,
          manualValue: updatedData.manualValue
        }
      });
      
      // Update Redux state with the appropriate value based on auto-calculate setting
      if (isChecked && updatedData.apiValue !== null) {
        // If auto-calculate is ON, use API value
        dispatch(
          setVeticalAverageElevation({
            index,
            value: updatedData.apiValue.toFixed(1),
          })
        );
      } else if (!isChecked && updatedManualValue !== null) {
        // If auto-calculate is OFF, use manual value
        dispatch(
          setVeticalAverageElevation({
            index,
            value: updatedManualValue.toFixed(1),
          })
        );
      }
      
      // If auto-calculation is turned on, trigger a refetch to update values
      if (isChecked) {
        refetchPolygonElevation(index);
      }
    } catch (error) {
      console.error("Error updating elevation auto-calculate:", error);
      // toast.error(t('verticalPvArea:errors.savingElevation'));
    }
  };

  const handleUpperHeightChange = async (index: number, value: string | null) => {
    try {
      // console.log(`Setting upper height for index ${index} to:`, value);
      
      // Update Redux state
      dispatch(
        setVerticalUpperHeight({
          index,
          floatValue: value,
        })
      );
      
      // Save to Firebase with special handling for heights
      const validProjectId = safeProjectId();

      if (validProjectId) {
        const upperValue = value !== null ? parseFloat(value) : null;
        
        // Get existing data to ensure we have the most current lower height
        const projectRef = doc(db, 'projects', projectId);
        const projectDoc = await getDoc(projectRef);
        const areaId = `VPV-${index + 1}`;
        
        let lowerValue = null;
        
        // First try to get the lower height from Firebase (most reliable)
        if (projectDoc.exists()) {
          const data = projectDoc.data();
          const areaData = data.verticalPVAreas && data.verticalPVAreas[areaId];
          
          if (areaData && areaData.heights && areaData.heights.lower !== undefined) {
            lowerValue = areaData.heights.lower;
            // console.log(`Retrieved lower height from Firebase for ${areaId}:`, lowerValue);
          }
        }
        
        // If not found in Firebase, use the Redux state value
        if (lowerValue === null && verticalLowerHeight[index] !== undefined && verticalLowerHeight[index] !== null) {
          lowerValue = typeof verticalLowerHeight[index] === 'string' 
            ? parseFloat(verticalLowerHeight[index]) 
            : verticalLowerHeight[index];
          // console.log(`Using Redux state lower height for ${areaId}:`, lowerValue);
        }
        
        // Save both height values to ensure consistency
        await saveVerticalPVArea(projectId, index, {
          heights: {
            upper: upperValue,
            lower: lowerValue
          }
        });
        
        // console.log(`Saved heights for ${areaId}:`, { upper: upperValue, lower: lowerValue });
      }
    } catch (error) {
      console.error(`Error in handleUpperHeightChange for index ${index}:`, error);
      toast.error(t('verticalPvArea:errors.savingHeights'));
    }
  };
  
  const handleLowerHeightChange = async (index: number, value: string | null) => {
    try {
      // console.log(`Setting lower height for index ${index} to:`, value);
      
      // Update Redux state
      dispatch(
        setVerticalLowerHeight({
          index,
          floatValue: value,
        })
      );
      
      // Save to Firebase with special handling for heights
      if (projectId) {
        const lowerValue = value !== null ? parseFloat(value) : null;
        
        // Get existing data to ensure we have the most current upper height
        const projectRef = doc(db, 'projects', projectId);
        const projectDoc = await getDoc(projectRef);
        const areaId = `VPV-${index + 1}`;
        
        let upperValue = null;
        
        // First try to get the upper height from Firebase (most reliable)
        if (projectDoc.exists()) {
          const data = projectDoc.data();
          const areaData = data.verticalPVAreas && data.verticalPVAreas[areaId];
          
          if (areaData && areaData.heights && areaData.heights.upper !== undefined) {
            upperValue = areaData.heights.upper;
            // console.log(`Retrieved upper height from Firebase for ${areaId}:`, upperValue);
          }
        }
        
        // If not found in Firebase, use the Redux state value
        if (upperValue === null && verticalUpperHeight[index] !== undefined && verticalUpperHeight[index] !== null) {
          upperValue = typeof verticalUpperHeight[index] === 'string' 
            ? parseFloat(verticalUpperHeight[index]) 
            : verticalUpperHeight[index];
          // console.log(`Using Redux state upper height for ${areaId}:`, upperValue);
        }
        
        // Save both height values to ensure consistency
        await saveVerticalPVArea(projectId, index, {
          heights: {
            upper: upperValue,
            lower: lowerValue
          }
        });
        
        // console.log(`Saved heights for ${areaId}:`, { upper: upperValue, lower: lowerValue });
      }
    } catch (error) {
      console.error(`Error in handleLowerHeightChange for index ${index}:`, error);
      toast.error(t('verticalPvArea:errors.savingHeights'));
    }
  };

  // Function to save manual elevation value
  const saveManualElevation = async (index: number, value: number | null) => {
    if (!projectId) return;
    
    try {
      // Get current elevation data
      const currentData = verticalElevationData[index] || {
        isAutoCalculated: false,
        manualValue: null,
        apiValue: null
      };
      
      // Create updated data - ensure isAutoCalculated is explicitly set to false
      const updatedData: VerticalElevationData = {
        ...currentData,
        isAutoCalculated: false,
        manualValue: value
      };
      
      // Update local state
      setVerticalElevationData(prev => ({
        ...prev,
        [index]: updatedData
      }));
      
      // Save to consolidated data in Firebase
      await saveVerticalPVArea(projectId, index, {
        elevation: {
          value: value,
          isAutoCalculated: false,
          apiValue: updatedData.apiValue,
          manualValue: value
        }
      });
      
      // Explicitly update Redux state for the elevation auto-calculate checkbox
      dispatch(
        setVerticalElevationCheck({
          index,
          check: false,
        })
      );
      
      // Update Redux with the manual value
      dispatch(
        setVeticalAverageElevation({
          index,
          value: value !== null ? value.toFixed(1) : null,
        })
      );
    } catch (error) {
      console.error("Error saving manual elevation:", error);
      // toast.error(t('verticalPvArea:errors.savingElevation'));
    }
  };
  
  // Function to save manual azimuth value
  const saveManualAzimuth = async (index: number, value: number | null) => {
    if (!projectId) return;

    try {
      // If value is null or undefined, don't save
      if (value === null || value === undefined) return;

      // Update Redux first
      dispatch(
        setVerticalAzimutyhValue({
          index,
          azimuth: value.toString(),
        })
      );

      // Check if we have elevation data for this polygon
      if (!elevationData[index]) {
        console.warn(`Cannot save manual azimuth for polygon ${index}: Missing elevation data`);
        return;
      }

      // Save to consolidated data in Firebase
      await saveVerticalPVArea(projectId, index, {
        azimuth: {
          value: value,
          isAutoCalculated: false
        },
        polygonData: elevationData[index]
      });
    } catch (error) {
      console.error(`Error saving manual azimuth for polygon ${index}:`, error);
      // toast.error(t('verticalPvArea:errors.savingAzimuth'));
    }
  };

  // Handle post-drag update
  const handlePostDragUpdate = (index: number) => {
    // Mark this polygon as having a pending elevation update
    pendingElevationUpdatesRef.current = {
      ...pendingElevationUpdatesRef.current,
      [index]: true
    };
    
    // Reset elevation fetch status to force a new fetch
    setElevationFetchStatus(prev => ({
      ...prev,
      [index]: 'pending'
    }));
    
    // Start with fetching elevation - we'll trigger azimuth calculation after that completes
    fetchElevations(index);
  };

  // Force azimuth calculation with type-safe loading indicator
// ✅ OPTIMIZED: Force azimuth calculation using BATCH API (more efficient)
const forceAzimuthCalculation = async (index: number): Promise<void> => {
  // Skip if we don't have required data
  if (
    !polygons[index] ||
    polygons[index].length !== 2 ||
    !elevationData[index] ||
    !elevationData[index].upperEdge ||
    !elevationData[index].lowerEdge ||
    verticalLowerHeight[index] === null ||
    verticalUpperHeight[index] === null
  ) {
    console.warn(`Missing required data for polygon ${index}`);
    return;
  }

  try {
    // ✅ Use batch API even for single polygon (more efficient and consistent)
    // Pass forceCalculate=true to skip the checkedAzimuth filter
    await calculateBatchAzimuth([index], true);
  } catch (error) {
    console.error(`Error calculating azimuth for polygon ${index}:`, error);
  }
};

  // Handler for azimuth auto-calculate change
  const handleAzimuthCheckChange = async (index: number, checked: boolean) => {
    // Update Redux state
    dispatch(
      setVerticalAzimuthCheck({
        index,
        check: checked,
      })
    );
  
    // Save to consolidated data in Firebase
    await saveVerticalPVArea(projectId, index, {
      azimuth: {
        value: verticalAzimuth[index] !== undefined ? verticalAzimuth[index] : null,
        isAutoCalculated: checked
      }
    });
  
    // If turning off auto-calculate, keep the current value as manual
    if (!checked) {
      // If there's a current value, store it as manual
      if (verticalAzimuth[index] !== null && verticalAzimuth[index] !== undefined) {
        const currentValue = verticalAzimuth[index];
        
        if (!isNaN(currentValue)) {
          await saveManualAzimuth(index, currentValue);
        }
      }
      return;
    }
    
    // ✅ FIX ISSUE #2: When turning ON auto-calculate, immediately recalculate
    // to override any manual values using BATCH API
    if (
      polygons[index] &&
      polygons[index].length === 2 &&
      elevationData[index] &&
      verticalLowerHeight[index] !== null &&
      verticalUpperHeight[index] !== null
    ) {
      // Use batch API even for single polygon (more efficient)
      // Pass forceCalculate=true to override the checkedAzimuth filter (state might not be updated yet)
      await calculateBatchAzimuth([index], true);
    } else {
      // If we don't have elevation data yet, fetch it first
      if (polygons[index]?.length === 2 && (!elevationData[index] || !elevationData[index]?.upperEdge?.elevation || !elevationData[index]?.lowerEdge?.elevation)) {
        await fetchElevations(index);

        // After fetching, try calculating azimuth again
        setTimeout(async () => {
          if (
            polygons[index] &&
            polygons[index].length === 2 &&
            elevationData[index] &&
            verticalLowerHeight[index] !== null &&
            verticalUpperHeight[index] !== null
          ) {
            await calculateBatchAzimuth([index], true);
          }
        }, 500);
      }
    }
  };

  // Monitor polygon changes to detect dragging
  useEffect(() => {
    // Only process if we have polygons and previous polygons to compare
    if (polygons.length > 0 && prevPolygonsRef.current.length > 0) {
      // Compare the current polygons with the previous ones
      polygons.forEach((polygon, index) => {
        const prevPolygon = prevPolygonsRef.current[index];
        if (!prevPolygon) return; // Skip if no previous polygon
        
        const prevUpperLat = prevPolygon[0]?.lat;
        const prevUpperLng = prevPolygon[0]?.lng;
        const prevLowerLat = prevPolygon[1]?.lat;
        const prevLowerLng = prevPolygon[1]?.lng;
        
        const newUpperLat = polygon[0]?.lat;
        const newUpperLng = polygon[0]?.lng;
        const newLowerLat = polygon[1]?.lat;
        const newLowerLng = polygon[1]?.lng;
        
        // Check if coordinates have changed
        const epsilon = 0.0000001; // Small threshold
        const hasChanged = 
          Math.abs(prevUpperLat - newUpperLat) > epsilon ||
          Math.abs(prevUpperLng - newUpperLng) > epsilon ||
          Math.abs(prevLowerLat - newLowerLat) > epsilon ||
          Math.abs(prevLowerLng - newLowerLng) > epsilon;
        
        // If coordinates have changed, we're in a dragging state
        if (hasChanged) {
          isDraggingRef.current = true;
          
          // Clear any existing timeout
          if (dragTimeoutRef.current) {
            clearTimeout(dragTimeoutRef.current);
          }
          
          // Set a new timeout - if no changes for 500ms, consider drag finished
          dragTimeoutRef.current = setTimeout(() => {
            isDraggingRef.current = false;
            
            // Handle the post-drag update in a controlled sequence
            handlePostDragUpdate(index);
          }, 500);
        }
      });
    }
    
    // Update previous polygons reference
    prevPolygonsRef.current = JSON.parse(JSON.stringify(polygons));
  }, [polygons]);

  // Fetch elevations for polygon points
  const fetchElevations = async (specificIndex?: number) => {
    try {
      // If a specific index is provided, only fetch that one polygon
      const polygonsToProcess = specificIndex !== undefined 
        ? [{ polygon: polygons[specificIndex], index: specificIndex }]
        : polygons.map((polygon, index) => ({ polygon, index }));
      
      // Process each requested polygon
      for (const { polygon, index } of polygonsToProcess) {
        // Skip if already processed and not specifically requested
        if (elevationFetchStatus[index] === 'complete' && specificIndex !== index) {
          continue;
        }
        
        setElevationFetchStatus(prev => ({
          ...prev,
          [index]: 'fetching'
        }));
        
        try {
          // Fetch elevation data
          const upperCoords = polygon[0];
          const lowerCoords = polygon[1];
          
          if (!upperCoords?.lat || !upperCoords?.lng || !lowerCoords?.lat || !lowerCoords?.lng) {
            console.error(`Invalid coordinates for polygon ${index}`);
            setElevationFetchStatus(prev => ({
              ...prev,
              [index]: 'error'
            }));
            continue;
          }

          // Prepare both points for batch elevation request
          const points = [
            { lat: upperCoords.lat, lng: upperCoords.lng },
            { lat: lowerCoords.lat, lng: lowerCoords.lng }
          ];

          // Make single batch API call for both points
          const response = await batchApis.getBatchElevation(points);
          const elevations = response.data.elevations;

          // Validate we got both elevations
          if (!elevations || elevations.length !== 2 || elevations.some((e: number | null) => e === null)) {
            console.error(`Failed to fetch elevations for polygon ${index}`, elevations);
            setElevationFetchStatus(prev => ({
              ...prev,
              [index]: 'error'
            }));
            continue;
          }

          const upperElevation = elevations[0];
          const lowerElevation = elevations[1];
          
          // Calculate average elevation
          const averageElevationValue = (Number(lowerElevation) + Number(upperElevation)) / 2;
          
          // Update the elevation data
          const newElevationData: ElevationData = {
            upperEdge: {
              elevation: upperElevation,
              lat: upperCoords.lat,
              lon: upperCoords.lng,
              offset: 0,
              tag: "upper_edge",
            },
            lowerEdge: {
              elevation: lowerElevation,
              lat: lowerCoords.lat,
              lon: lowerCoords.lng,
              offset: 0,
              tag: "lower_edge",
            },
          };
          
          // Update state
          setElevationData(prev => {
            const newData = [...prev];
            newData[index] = newElevationData;
            return newData;
          });
          
          // Check current auto-calculate setting
          const isAutoCalculated = checkedAvgElevation[index] !== undefined 
            ? checkedAvgElevation[index] 
            : true;
            
          // Get current elevation data from state
          const currentData = verticalElevationData[index] || {
            isAutoCalculated: isAutoCalculated,
            manualValue: null,
            apiValue: null
          };
          
          // Create updated data object
          const updatedData: VerticalElevationData = {
            ...currentData,
            isAutoCalculated: isAutoCalculated,
            apiValue: averageElevationValue,
          };
          
          // Only update manual value if it's null and we're turning OFF auto-calculate
          if (updatedData.manualValue === null && !isAutoCalculated) {
            updatedData.manualValue = averageElevationValue;
          }
          
          // Update local state
          setVerticalElevationData(prev => ({
            ...prev,
            [index]: updatedData
          }));
          
          // Save to consolidated data in Firebase
          if (projectId) {
            await saveVerticalPVArea(projectId, index, {
              elevation: {
                value: isAutoCalculated ? averageElevationValue : updatedData.manualValue,
                isAutoCalculated: isAutoCalculated,
                apiValue: averageElevationValue,
                manualValue: updatedData.manualValue
              },
              polygonData: newElevationData
            });
          }
          
          // Update Redux with the appropriate value based on auto-calculate setting
          if (isAutoCalculated) {
            dispatch(
              setVeticalAverageElevation({
                index,
                value: averageElevationValue.toFixed(1),
              })
            );
          } else if (updatedData.manualValue !== null) {
            dispatch(
              setVeticalAverageElevation({
                index,
                value: updatedData.manualValue.toFixed(1),
              })
            );
          }
          
          // Mark as complete
          setElevationFetchStatus(prev => ({
            ...prev,
            [index]: 'complete'
          }));
          
          // Check if this was a post-drag update
          if (pendingElevationUpdatesRef.current[index]) {
            // If azimuth auto-calculate is enabled, trigger azimuth calculation
            if (checkedAzimuth[index]) {
              // Small delay to ensure state updates are processed
              setTimeout(() => {
                forceAzimuthCalculation(index);
              }, 300);
            }
            
            // Clear the pending flag
            pendingElevationUpdatesRef.current = {
              ...pendingElevationUpdatesRef.current,
              [index]: false
            };
          }
        } catch (error) {
          console.error(`Error fetching elevation for polygon ${index}:`, error);
          setElevationFetchStatus(prev => ({
            ...prev,
            [index]: 'error'
          }));
        }
      }
    } catch (error) {
      console.error("Error in fetchElevations:", error);
    }
  };

  // Calculate azimuth for a specific polygon
  const calculateAzimuthForPolygon = async (index: number): Promise<void> => {
    // Skip if auto-calculate is not enabled
    if (!checkedAzimuth[index]) {
      return;
    }
    
    // Skip if we don't have complete data
    if (
      !polygons[index] || 
      polygons[index].length !== 2 || 
      !elevationData[index] ||
      verticalLowerHeight[index] === null || 
      verticalUpperHeight[index] === null
    ) {
      return;
    }

    try {
      const slopeAngle = vertivalSlopeArea[index] !== null ? parseFloat(vertivalSlopeArea[index]) : 90;
      
      const result = {
        area_type: "vertical",
        point1: {
          lat: elevationData[index]?.upperEdge?.lat,
          lon: elevationData[index]?.upperEdge?.lon,
          elevation: elevationData[index]?.upperEdge?.elevation,
          offset: 0,
          tag: "upper_edge",
        },
        point2: {
          lat: elevationData[index]?.lowerEdge?.lat, 
          lon: elevationData[index]?.lowerEdge?.lon,  
          elevation: elevationData[index]?.lowerEdge?.elevation,
          offset: 0,
          tag: "upper_edge",
        },
        point3: {
          lat: elevationData[index]?.lowerEdge?.lat,
          lon: elevationData[index]?.lowerEdge?.lon,
          elevation: elevationData[index]?.lowerEdge?.elevation,
          offset: 0,
          tag: "lower_edge",
        },
        point4: {
          lat: elevationData[index]?.upperEdge?.lat, 
          lon: elevationData[index]?.upperEdge?.lon,
          elevation: elevationData[index]?.upperEdge?.elevation,
          offset: 0,
          tag: "lower_edge",
        },
        slope_angle: slopeAngle
      };
      
      // Make the API call
      const responseData = await putData<AzimuthResponse, typeof result>(
        '/azimuth/',
        result,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      // Validate the API response
      if (!responseData || responseData.azimuth === undefined || responseData.azimuth === null) {
        console.error('Invalid API response:', responseData);
        return;
      }

      const azimuthResult = responseData.azimuth;

      // Convert to a number and check if it's valid
      const numericAzimuth = Number(azimuthResult);
      if (isNaN(numericAzimuth)) {
        console.error(`Invalid azimuth value: ${azimuthResult}`);
        return;
      }

      // Now we have a valid number, format it properly
      const formattedValue = numericAzimuth.toFixed(1);

      // Use formattedValue for Redux update
      dispatch(
        setVerticalAzimutyhValue({
          index,
          azimuth: formattedValue,
        })
      );
      
      // Save to consolidated data in Firebase
      if (projectId) {
        await saveVerticalPVArea(projectId, index, {
          azimuth: {
            value: numericAzimuth,
            isAutoCalculated: true
          },
          polygonData: elevationData[index]
        });
      }
    } catch (error) {
      console.error(`Error calculating azimuth for polygon ${index}:`, error);
      // toast.error(t('verticalPvArea:errors.calculatingAzimuth'));
    }
  };

  // Calculate azimuth for multiple polygons in batch
  const calculateBatchAzimuth = async (indices: number[], forceCalculate: boolean = false): Promise<void> => {
    // Filter to only include polygons with complete data
    // If forceCalculate is true (from toggle), skip the checkedAzimuth check
    const validIndices = indices.filter(index => {
      return (
        (forceCalculate || checkedAzimuth[index]) &&
        polygons[index]?.length === 2 &&
        elevationData[index] &&
        verticalLowerHeight[index] !== null &&
        verticalUpperHeight[index] !== null
      );
    });

    if (validIndices.length === 0) return;

    try {
      // Prepare batch data
      const pvAreas = validIndices.map(index => {
        const slopeAngle = vertivalSlopeArea[index] !== null ? parseFloat(vertivalSlopeArea[index]) : 90;

        const areaData: any = {
          index: index,
          area_type: "vertical",
          slope_angle: slopeAngle,
          point1: {
            lat: elevationData[index]?.upperEdge?.lat,
            lng: elevationData[index]?.upperEdge?.lon,
            elevation: elevationData[index]?.upperEdge?.elevation,
            offset: 0,
            tag: "upper_edge",
          },
          point2: {
            lat: elevationData[index]?.lowerEdge?.lat,
            lng: elevationData[index]?.lowerEdge?.lon,
            elevation: elevationData[index]?.lowerEdge?.elevation,
            offset: 0,
            tag: "upper_edge",
          },
          point3: {
            lat: elevationData[index]?.lowerEdge?.lat,
            lng: elevationData[index]?.lowerEdge?.lon,
            elevation: elevationData[index]?.lowerEdge?.elevation,
            offset: 0,
            tag: "lower_edge",
          },
          point4: {
            lat: elevationData[index]?.upperEdge?.lat,
            lng: elevationData[index]?.upperEdge?.lon,
            elevation: elevationData[index]?.upperEdge?.elevation,
            offset: 0,
            tag: "lower_edge",
          },
        };

        return areaData;
      });

      // Call batch API
      const response = await batchApis.getBatchAzimuth(pvAreas);

      // Process results
      if (response.data?.results) {
        for (const result of response.data.results) {
          if (result.success && result.azimuth !== null) {
            const index = result.index;
            const numericAzimuth = Number(result.azimuth);
            const formattedValue = numericAzimuth.toFixed(1);

            // Update Redux
            dispatch(
              setVerticalAzimutyhValue({
                index,
                azimuth: formattedValue,
              })
            );

            // Save to Firebase
            if (projectId) {
              await saveVerticalPVArea(projectId, index, {
                azimuth: {
                  value: numericAzimuth,
                  isAutoCalculated: true
                },
                polygonData: elevationData[index]
              });
            }
          } else {
            console.error(`Batch azimuth calculation failed for polygon ${result.index}:`, result.error);
          }
        }
      }
    } catch (error) {
      console.error('Error in batch azimuth calculation:', error);
      // Fallback to individual calculations
      for (const index of validIndices) {
        await calculateAzimuthForPolygon(index);
      }
    }
  };

  // Trigger azimuth recalculation when data changes
  // ✅ FIX: Only recalculate azimuth when POLYGON COORDINATES change, not heights!
  // Azimuth (direction) is determined by the polygon's position, not its height or slope
  useEffect(() => {
    // Skip if no polygons
    if (polygons.length === 0) return;

    // Collect all polygon indices that need azimuth update
    const indicesToUpdate: number[] = [];
    polygons.forEach((_, index) => {
      if (checkedAzimuth[index]) {
        indicesToUpdate.push(index);
      }
    });

    if (indicesToUpdate.length === 0) return;

    // Use a debounced trigger to avoid too many API calls
    const timeoutId = setTimeout(() => {
      // Calculate batch azimuth for all polygons at once
      calculateBatchAzimuth(indicesToUpdate);
    }, 500); // Debounce time

    return () => clearTimeout(timeoutId);
  }, [
    // ✅ ONLY depend on elevation data (polygon coordinates)
    // Heights and slopes DON'T affect azimuth/direction!
    JSON.stringify(elevationData)
  ]);
  
  // Add a debounced trigger for azimuth updates
  const triggerAzimuthUpdate = (index: number): void => {
    // Skip if auto-calculate is not enabled
    if (!checkedAzimuth[index]) return;
    
    // Clear any existing timer for this index
    const existingTimer = azimuthDebounceTimers[index];
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // Create a new timer
    const timer = setTimeout(() => {
      calculateAzimuthForPolygon(index);
      
      // Clear the timer reference when done
      setAzimuthDebounceTimers(prev => ({
        ...prev,
        [index]: null
      }));
    }, 500); // Debounce delay of 500ms
    
    // Store the timer reference
    setAzimuthDebounceTimers(prev => ({
      ...prev,
      [index]: timer
    }));
  };
  
  // Function to handle azimuth field blur (for manual entry)
  const handleAzimuthBlur = (index: number, value: string) => {
    if (!checkedAzimuth[index] && value.trim() !== "" && value !== " ") {
      // Save manual azimuth to Firebase
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        saveManualAzimuth(index, numValue);
      }
    }
  };
  
  // ✅ FIX: Remove azimuth trigger - heights/slopes don't affect direction!
  // Azimuth only depends on polygon coordinates, not height or slope values
  const handleFieldBlur = (index: number): void => {
    // Heights and slopes don't affect azimuth, so no calculation needed
    // Azimuth is only recalculated when polygon coordinates change
  };

  // Load existing data from Firebase when component mounts
  useEffect(() => {
    const loadVerticalDataFromFirebase = async () => {
       if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
      console.error("Invalid project ID, cannot load data from Firebase");
      return;
    }
    
      try {
        setIsLoading(true);
        
        // Check if migration is needed
        await migrateToConsolidatedVerticalStructure(projectId);
        
        // Load data from the consolidated structure
        const verticalPVAreas = await loadVerticalPVAreas(projectId);
        
        // If there are no areas in Firebase but we have polygons in Redux, 
        // we need to clear the Redux state to avoid inconsistency
        if (Object.keys(verticalPVAreas).length === 0 && polygons.length > 0) {
          // console.log("No areas in Firebase but polygons in Redux, clearing Redux state");
          
          for (let i = polygons.length - 1; i >= 0; i--) {
            dispatch(removeVerticalPolygon(i));
            dispatch(deleteVerticalHeights({ index: i }));
            dispatch(deleteAzimuthValue({ index: i }));
            dispatch(removeVerticalAvgElevation({ index: i }));
            dispatch(deleteVerticalchecked({ index: i }));
            dispatch(deleteVerticalSwap({ index: i }));
          }
        } else {
          // Process each area and update Redux state
          Object.entries(verticalPVAreas).forEach(([areaId, data]) => {
            const index = parseInt(areaId.replace('VPV-', '')) - 1;
            
            // Update polygon if available
            if (data.polygon && data.polygon.length > 0) {
              dispatch(
                addVerticalPolygon({
                  index,
                  verticalPolygon: data.polygon
                })
              );
            }
            
            // Update azimuth
            if (data.azimuth) {
              // Update azimuth value if it exists
              if (data.azimuth.value !== null) {
                dispatch(
                  setVerticalAzimutyhValue({
                    index,
                    azimuth: data.azimuth.value.toString()
                  })
                );
              }
              
              // Update auto-calculate setting
              dispatch(
                setVerticalAzimuthCheck({
                  index,
                  check: data.azimuth.isAutoCalculated
                })
              );
            }
            
            // Update elevation
            if (data.elevation) {
              // Update auto-calculate setting
              dispatch(
                setVerticalElevationCheck({
                  index,
                  check: data.elevation.isAutoCalculated
                })
              );
              
              // Set elevation value based on auto/manual setting
              const elevationValue = data.elevation.isAutoCalculated 
                ? data.elevation.apiValue 
                : data.elevation.manualValue;
                
              if (elevationValue !== null) {
                dispatch(
                  setVeticalAverageElevation({
                    index,
                    value: elevationValue.toFixed(1)
                  })
                );
              }
              
              // Update local state
              setVerticalElevationData(prev => ({
                ...prev,
                [index]: data.elevation ? {
                  isAutoCalculated: data.elevation.isAutoCalculated,
                  apiValue: data.elevation.apiValue,
                  manualValue: data.elevation.manualValue
                } : prev[index] || {}
              }));
            }
            
            // Update heights if available
            if (data.heights) {
              // Log before updating to help with debugging
              // console.log(`Setting heights for ${areaId}:`, data.heights);
              
              // Update upper height
              if (data.heights.upper !== null && data.heights.upper !== undefined) {
                dispatch(
                  setVerticalUpperHeight({
                    index,
                    floatValue: data.heights.upper.toString()
                  })
                );
              }
              
              // Update lower height
              if (data.heights.lower !== null && data.heights.lower !== undefined) {
                dispatch(
                  setVerticalLowerHeight({
                    index,
                    floatValue: data.heights.lower.toString()
                  })
                );
              }
            }
            
            // Update slope
            if (data.slope !== undefined && data.slope !== null) {
              dispatch(
                addVerticalSlopeArea({
                  index,
                  value: data.slope.toString()
                })
              );
            }
            
            // Update swapped status
            dispatch(
              setVerticalSwap({
                index,
                checked: data.swapped || false
              })
            );
            
            // Update elevation data if available
            if (data.elevationData) {
              setElevationData(prev => {
                const newData = [...prev];
                newData[index] = data.elevationData as ElevationData;
                return newData;
              });
              
              // Mark elevation as complete
              setElevationFetchStatus(prev => ({
                ...prev,
                [index]: 'complete'
              }));
            }
          });
        }
        
        // After loading, ensure Firebase and Redux are in sync
        await reconcileFirebaseWithReduxState();
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading vertical polygon data from Firebase:", error);
        setIsLoading(false);
        toast.error(t('verticalPvArea:errors.loadingData'));
      }
    };

    loadVerticalDataFromFirebase();
  }, [dispatch, projectId]);
  
  // Trigger elevation fetching only when polygon count changes or auto-calculation is toggled
  useEffect(() => {
    // This effect only runs when polygons are added/removed or auto-calculate is toggled
    // It won't run during dragging operations since we handle that separately
    let timeout: NodeJS.Timeout | null = null;

    const delayedFetch = () => {
      if (timeout) clearTimeout(timeout);
      
      // Only fetch if we're not in the middle of a drag operation
      if (!isDraggingRef.current) {
        timeout = setTimeout(() => {
          fetchElevations();
        }, 1000);
      }
    };

    delayedFetch();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [polygons.length, checkedAvgElevation]);
  
  // Save data when component unmounts
  useEffect(() => {
    // Create a function to save all data to Firebase
    const saveAllDataToFirebase = async () => {
      
      if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
        console.error('Invalid projectId in saveAllDataToFirebase:', projectId);
        return;
      }
      
      // Process each polygon
      for (let i = 0; i < polygons.length; i++) {
        // Ensure proper type conversion for height values
        let upperHeight = null;
        if (verticalUpperHeight[i] !== undefined && verticalUpperHeight[i] !== null) {
          upperHeight = typeof verticalUpperHeight[i] === 'string' ? 
            parseFloat(verticalUpperHeight[i]) : verticalUpperHeight[i];
        }
        
        let lowerHeight = null;
        if (verticalLowerHeight[i] !== undefined && verticalLowerHeight[i] !== null) {
          lowerHeight = typeof verticalLowerHeight[i] === 'string' ? 
            parseFloat(verticalLowerHeight[i]) : verticalLowerHeight[i];
        }
        
        // Create data object for this polygon
        const areaData: Partial<ConsolidatedVerticalPVArea> = {
          polygon: polygons[i] || [],
          azimuth: {
            value: verticalAzimuth[i] !== undefined ? verticalAzimuth[i] : null,
            isAutoCalculated: Boolean(checkedAzimuth[i])
          },
          elevation: {
            value: averageElevation[i] !== undefined ? averageElevation[i] : null,
            isAutoCalculated: Boolean(checkedAvgElevation[i]),
            apiValue: verticalElevationData[i]?.apiValue ?? null,
            manualValue: verticalElevationData[i]?.manualValue ?? null
          },
          heights: {
            upper: upperHeight,
            lower: lowerHeight
          },
          
          // Ensure correct type handling for slope
          slope: vertivalSlopeArea[i] !== undefined && vertivalSlopeArea[i] !== null ? 
            (typeof vertivalSlopeArea[i] === 'string' ? 
              parseFloat(vertivalSlopeArea[i]) : 
              vertivalSlopeArea[i]) : null,
              
          // Ensure boolean conversion for swapped
          swapped: Boolean(verticalSwap[i]),
          polygonData: elevationData[i] || null
        };
        
        // Save to Firebase
        await saveVerticalPVArea(projectId, i, areaData);
      }
    };
    
    return () => {
      // Save all data when component unmounts
      saveAllDataToFirebase();
      
      // Clear all timers
      Object.values(azimuthDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);
  
  // Update the useEffect that detects new polygons to mark them for elevation fetching
  useEffect(() => {
    // ✅ FIX: Skip during initial data load from Firebase
    if (isLoading) return;

    // When a new polygon is added, mark it as needing elevation fetching
    const lastIndex = polygons.length - 1;
    if (lastIndex >= 0 && !elevationFetchStatus[lastIndex]) {
      // ✅ FIX: Only mark as pending if we don't already have valid elevation data
      // This prevents redundant API calls when loading existing data from Firebase
      const hasValidElevationData =
        elevationData[lastIndex] &&
        elevationData[lastIndex].upperEdge &&
        elevationData[lastIndex].lowerEdge &&
        elevationData[lastIndex].upperEdge?.elevation !== null &&
        elevationData[lastIndex].lowerEdge?.elevation !== null;

      // Only fetch if we genuinely don't have elevation data yet
      if (!hasValidElevationData) {
        setElevationFetchStatus(prev => ({
          ...prev,
          [lastIndex]: 'pending'
        }));
      } else {
        // We already have valid data from Firebase, mark as complete
        setElevationFetchStatus(prev => ({
          ...prev,
          [lastIndex]: 'complete'
        }));
      }
    }
  }, [polygons.length, isLoading, elevationFetchStatus, elevationData]);
  
  // Handle the elevation fetching in a separate useEffect
  useEffect(() => {
    const pendingPolygons = Object.entries(elevationFetchStatus)
      .filter(([_, status]) => status === 'pending')
      .map(([index]) => parseInt(index));
    
    if (pendingPolygons.length > 0) {
      pendingPolygons.forEach(index => {
        setTimeout(() => {
          fetchElevations(index);
        }, 300 * index); // Stagger the requests slightly
      });
    }
  }, [elevationFetchStatus]);
  
  // Initialize default settings when a new polygon is added
  useEffect(() => {
    const polygonLength = polygons.length - 1;
    if (polygonLength >= 0) {
      dispatch(setVerticalSwap({ index: polygonLength, checked: false }));
      if (checkedAvgElevation[polygonLength] === undefined) {
        dispatch(
          setVerticalElevationCheck({ index: polygonLength, check: true })
        );
      }
      if (checkedAzimuth[polygonLength] === undefined) {
        dispatch(setVerticalAzimuthCheck({ index: polygonLength, check: true }));
      }
    }
  }, [polygons.length]);
  
  // Calculate width for each polygon and store in widthValues state
  useEffect(() => {
    const newWidthValues = polygons.map((coord, index) => {
      if (!google?.maps?.geometry?.spherical) return 0;
      
      const width = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(coord[0]?.lat, coord[0]?.lng),
        new google.maps.LatLng(coord[1]?.lat, coord[1]?.lng)
      );
      // Add null check before calling toFixed()
      return width !== undefined && width !== null ? parseFloat(width.toFixed(1)) : 0;
    });
    setWidthValues(newWidthValues);
  }, [polygons]);

  // Set initial dropdown expansion states
  useEffect(() => {
    if (polygons.length > 0 && polygons.length >= polygonLength) {
      setIsDropdownExpanded((prev) => [
        ...new Array(polygons.length - 1).fill(false),
        true,
      ]);
      setPolygonLength(polygons.length);
    }
  }, [polygons.length]);

  // Toggle dropdown function
  const toggleDropdown = (index: number) => {
    const newDropdownState = [...isDropdownExpanded];
    newDropdownState[index] = !newDropdownState[index];
    setIsDropdownExpanded(newDropdownState);
  };

  // Handle swapping edges
  const handleSwapEdges = (index: number, value: any, isChecked: boolean) => {
    // Swap the coordinates
    const newUpperCoords = value[1];
    const newLowerCoords = value[0];
    
    dispatch(
      addVerticalPolygon({
        index,
        verticalPolygon: [
          {
            lat: newUpperCoords.lat,
            lng: newUpperCoords.lng,
          },
          {
            lat: newLowerCoords.lat,
            lng: newLowerCoords.lng,
          },
        ],
      })
    );
    
    // Reset the elevation fetch status
    setElevationFetchStatus(prev => ({
      ...prev,
      [index]: 'pending'
    }));
    
    // Update Redux state
    dispatch(
      setVerticalSwap({
        index,
        checked: isChecked,
      })
    );
    
    // Save swap status to Firebase - ensure it's a boolean
    saveVerticalPVArea(projectId, index, {
      swapped: Boolean(isChecked)
    });
    
    // Add debug logging to verify the value
    // console.log(`Saving swapped status for polygon ${index}:`, Boolean(isChecked));
    
    // Force re-fetch after a short delay
    setTimeout(() => {
      refetchPolygonElevation(index);
    }, 300);
  };
  
  // Delete polygon and all related data
  const handleDeletePolygon = async (index: number) => {
    try {
      // console.log(`Deleting vertical polygon at index ${index}. Current polygons count:`, polygons.length);
      
      // Get the areaId before deleting from Redux
      const areaId = `VPV-${index + 1}`;
      // console.log(`Area ID to delete: ${areaId}`);
      
      // Update Redux state to remove the polygon
      dispatch(removeVerticalPolygon(index));
      dispatch(deleteVerticalHeights({ index }));
      dispatch(deleteAzimuthValue({ index }));
      dispatch(removeVerticalAvgElevation({ index }));
      dispatch(deleteVerticalchecked({ index }));
      dispatch(deleteVerticalSwap({ index }));
    
      // Delete from Firebase
      if (projectId) {
        // Double-check that the project exists
        const projectRef = doc(db, 'projects', projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (!projectDoc.exists()) {
          console.error("Project does not exist");
          return;
        }
        
        const data = projectDoc.data();
        const allAreas = data.verticalPVAreas || {};
        
        // console.log("Current areas in Firebase:", Object.keys(allAreas));
        
        // Check if the area exists
        if (!allAreas[areaId]) {
          console.warn(`Area ${areaId} not found in Firebase`);
          return;
        }
        
        // If this is the only area, remove the entire verticalPVAreas field
        if (Object.keys(allAreas).length === 1) {
          // console.log("Only one area left, removing entire verticalPVAreas field");
          await updateDoc(projectRef, {
            verticalPVAreas: deleteField()
          });
        } else {
          // Create a new object without the deleted area
          const remainingAreas: Record<string, any> = {};
          
          // Only copy areas that should be kept
          Object.entries(allAreas).forEach(([id, value]) => {
            if (id !== areaId) {
              remainingAreas[id] = value;
            }
          });
          
          // console.log(`Remaining areas after deletion:`, Object.keys(remainingAreas));
          
          // Two-step process to ensure clean deletion
          // 1. First delete the entire verticalPVAreas field
          await updateDoc(projectRef, {
            verticalPVAreas: deleteField()
          });
          
          // 2. Then add back the remaining areas
          if (Object.keys(remainingAreas).length > 0) {
            await updateDoc(projectRef, {
              verticalPVAreas: remainingAreas
            });
          }
        }
        
        // console.log(`Successfully deleted area ${areaId} from Firebase`);
      }
      
      // Update local state by rebuilding objects
      const updatedElevationData = [...elevationData];
      updatedElevationData.splice(index, 1);
      setElevationData(updatedElevationData);
      
      const updatedDropdownExpanded = [...isDropdownExpanded];
      updatedDropdownExpanded.splice(index, 1);
      setIsDropdownExpanded(updatedDropdownExpanded);
      
      // Create clean objects for all state with indices
      const updatedVerticalElevationData: Record<number, VerticalElevationData> = {};
      const updatedFetchStatus: {[index: number]: 'pending' | 'fetching' | 'complete' | 'error'} = {};
      const updatedCalculatingAzimuth: {[index: number]: boolean} = {};
      const updatedAzimuthDebounceTimers: {[key: number]: NodeJS.Timeout | null} = {};
      
      // Rebuild all indexed state objects
      Object.entries(verticalElevationData).forEach(([keyStr, value]) => {
        const key = parseInt(keyStr);
        if (key < index) {
          updatedVerticalElevationData[key] = value;
        } else if (key > index) {
          updatedVerticalElevationData[key - 1] = value;
        }
      });
      
      Object.entries(elevationFetchStatus).forEach(([keyStr, value]) => {
        const key = parseInt(keyStr);
        if (key < index) {
          updatedFetchStatus[key] = value;
        } else if (key > index) {
          updatedFetchStatus[key - 1] = value;
        }
      });
      
      Object.entries(calculatingAzimuth).forEach(([keyStr, value]) => {
        const key = parseInt(keyStr);
        if (key < index) {
          updatedCalculatingAzimuth[key] = value;
        } else if (key > index) {
          updatedCalculatingAzimuth[key - 1] = value;
        }
      });
      
      Object.entries(azimuthDebounceTimers).forEach(([keyStr, timer]) => {
        const key = parseInt(keyStr);
        if (key === index && timer) {
          clearTimeout(timer);
        } else if (key < index) {
          updatedAzimuthDebounceTimers[key] = timer;
        } else if (key > index) {
          updatedAzimuthDebounceTimers[key - 1] = timer;
        }
      });
      
      // Update all state
      setVerticalElevationData(updatedVerticalElevationData);
      setElevationFetchStatus(updatedFetchStatus);
      setCalculatingAzimuth(updatedCalculatingAzimuth);
      setAzimuthDebounceTimers(updatedAzimuthDebounceTimers);
      
      // console.log("State updated after deletion");
    } catch (error) {
      console.error(`Error deleting vertical polygon:`, error);
      toast.error(t('verticalPvArea:errors.deletingPolygon'));
    }
  };


  //This useEffect to handle cleaning up the Firebase data if needed
  useEffect(() => {
    // If there are no polygons but we still have verticalPVAreas in Firebase, clean them up
    const cleanupFirebaseIfNeeded = async () => {
      if (polygons.length === 0 && projectId) {
        try {
          // console.log("No polygons left, ensuring Firebase is clean...");
          const projectRef = doc(db, 'projects', projectId);
          const projectDoc = await getDoc(projectRef);
          
          if (projectDoc.exists()) {
            const data = projectDoc.data();
            if (data.verticalPVAreas && Object.keys(data.verticalPVAreas).length > 0) {
              // console.log("Found leftover verticalPVAreas in Firebase, cleaning up");
              
              // Completely remove the verticalPVAreas field
              await updateDoc(projectRef, {
                verticalPVAreas: deleteField()
              });
              
              // console.log("Successfully removed all verticalPVAreas from Firebase");
            }
          }
        } catch (error) {
          console.error("Error cleaning up Firebase:", error);
        }
      }
    };
    
    cleanupFirebaseIfNeeded();
  }, [polygons.length, projectId]);


  useEffect(() => {
    if (polygons.length > 0) {
      // console.log('Current vertical slope values:', vertivalSlopeArea);
      // console.log('Current vertical swap values:', verticalSwap);
    }
  }, [polygons.length, vertivalSlopeArea, verticalSwap]);
  
  
  // Helper function for handling numeric input
  const handleNumericInput = (value: string): string => {
    const normalizedValue = value.replace(/,/g, '.');
    
    // Ensure only one decimal point exists
    let parts = normalizedValue.split('.');
    if (parts.length > 2) {
      // If multiple decimal points, keep only the first one
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    return normalizedValue;
  };

  // Helper function for limiting to one digit after decimal point
  const oneDigitAfterPoint = (enterValue: string): string => {
    // First convert any commas to periods
    let value = handleNumericInput(enterValue);
    
    // Then apply the original decimal place limitation
    if (value.includes(".")) {
      const parts = value.split(".");
      if (parts[1] && parts[1].length > 1) {
        value = `${parts[0]}.${parts[1].slice(0, 1)}`;
      }
    }
    return value;
  };

  const reconcileFirebaseWithReduxState = async () => {
    if (!projectId) return;
    
    try {
      // console.log("Reconciling Firebase data with Redux state...");
      
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        // console.log("Project document does not exist");
        return;
      }
      
      const data = projectDoc.data();
      const firebaseAreas = data.verticalPVAreas || {};
      
      // If Redux has no polygons but Firebase has areas, clean Firebase
      if (polygons.length === 0 && Object.keys(firebaseAreas).length > 0) {
        // console.log("Redux has no polygons but Firebase has areas, cleaning Firebase");
        await updateDoc(projectRef, {
          verticalPVAreas: deleteField()
        });
        return;
      }
      
      // If Redux has polygons but Firebase has none or different count, sync to Firebase
      if (polygons.length > 0 && Object.keys(firebaseAreas).length !== polygons.length) {
        // console.log(`Redux has ${polygons.length} polygons but Firebase has ${Object.keys(firebaseAreas).length} areas, syncing to Firebase`);
        
        // Create a new areas object
        const updatedAreas: Record<string, any> = {};
        
        // Add each polygon from Redux
        for (let i = 0; i < polygons.length; i++) {
          const areaId = `VPV-${i + 1}`;
          
          // Start with any existing data
          const existingData = firebaseAreas[areaId] || {};
          
          // Create area data with current Redux state
          const areaData: Partial<ConsolidatedVerticalPVArea> = {
            ...existingData,
            polygon: polygons[i] || [],
            azimuth: {
              value: verticalAzimuth[i] !== undefined ? verticalAzimuth[i] : null,
              isAutoCalculated: Boolean(checkedAzimuth[i])
            },
            elevation: {
              value: averageElevation[i] !== undefined ? averageElevation[i] : null,
              isAutoCalculated: Boolean(checkedAvgElevation[i]),
              apiValue: verticalElevationData[i]?.apiValue ?? null,
              manualValue: verticalElevationData[i]?.manualValue ?? null
            },
            heights: {
              upper: verticalUpperHeight[i] !== undefined && verticalUpperHeight[i] !== null ? 
                (typeof verticalUpperHeight[i] === 'string' ? 
                  parseFloat(verticalUpperHeight[i]) : 
                  verticalUpperHeight[i]) : null,
              lower: verticalLowerHeight[i] !== undefined && verticalLowerHeight[i] !== null ? 
                (typeof verticalLowerHeight[i] === 'string' ? 
                  parseFloat(verticalLowerHeight[i]) : 
                  verticalLowerHeight[i]) : null
            },
            slope: vertivalSlopeArea[i] !== undefined && vertivalSlopeArea[i] !== null ? 
              (typeof vertivalSlopeArea[i] === 'string' ? 
                parseFloat(vertivalSlopeArea[i]) : 
                vertivalSlopeArea[i]) : null,
            swapped: Boolean(verticalSwap[i])
          };
          
          updatedAreas[areaId] = areaData;
        }
        
        // Two-step update to ensure clean data
        await updateDoc(projectRef, {
          verticalPVAreas: deleteField()
        });
        
        if (Object.keys(updatedAreas).length > 0) {
          await updateDoc(projectRef, {
            verticalPVAreas: updatedAreas
          });
        }
        
        // console.log("Successfully reconciled Firebase with Redux state");
      }
    } catch (error) {
      console.error("Error reconciling Firebase with Redux state:", error);
    }
  };

  useEffect(() => {
    // Create a function to save all data to Firebase
    const saveAllDataToFirebase = async () => {
      if (!projectId) return;
      
      try {
        // First reconcile to ensure consistency
        await reconcileFirebaseWithReduxState();
        
        // Then save each polygon's data
        for (let i = 0; i < polygons.length; i++) {
          // Ensure proper type conversion for height values
          let upperHeight = null;
          if (verticalUpperHeight[i] !== undefined && verticalUpperHeight[i] !== null) {
            upperHeight = typeof verticalUpperHeight[i] === 'string' ? 
              parseFloat(verticalUpperHeight[i]) : verticalUpperHeight[i];
          }
          
          let lowerHeight = null;
          if (verticalLowerHeight[i] !== undefined && verticalLowerHeight[i] !== null) {
            lowerHeight = typeof verticalLowerHeight[i] === 'string' ? 
              parseFloat(verticalLowerHeight[i]) : verticalLowerHeight[i];
          }
          
          // Create data object for this polygon
          const areaData: Partial<ConsolidatedVerticalPVArea> = {
            polygon: polygons[i] || [],
            azimuth: {
              value: verticalAzimuth[i] !== undefined ? verticalAzimuth[i] : null,
              isAutoCalculated: Boolean(checkedAzimuth[i])
            },
            elevation: {
              value: averageElevation[i] !== undefined ? averageElevation[i] : null,
              isAutoCalculated: Boolean(checkedAvgElevation[i]),
              apiValue: verticalElevationData[i]?.apiValue ?? null,
              manualValue: verticalElevationData[i]?.manualValue ?? null
            },
            heights: {
              upper: upperHeight,
              lower: lowerHeight
            },
            slope: vertivalSlopeArea[i] !== undefined && vertivalSlopeArea[i] !== null ? 
              (typeof vertivalSlopeArea[i] === 'string' ? 
                parseFloat(vertivalSlopeArea[i]) : 
                vertivalSlopeArea[i]) : null,
            swapped: Boolean(verticalSwap[i]),
            polygonData: elevationData[i] || null
          };
          
          // Save to Firebase
          await saveVerticalPVArea(projectId, i, areaData);
        }
      } catch (error) {
        console.error("Error saving all data to Firebase:", error);
      }
    };
    
    return () => {
      // Save all data when component unmounts
      saveAllDataToFirebase();
      
      // Clear all timers
      Object.values(azimuthDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [
    projectId,
    polygons,
    verticalAzimuth,
    checkedAzimuth,
    averageElevation,
    checkedAvgElevation,
    verticalElevationData,
    verticalUpperHeight,
    verticalLowerHeight,
    vertivalSlopeArea,
    verticalSwap,
    elevationData
  ]);
  
  // Tooltips
  const groundElevationTooltip = (
    <Tooltip id="tooltip">
      <small>
        {t('verticalPvArea:tooltips.groundElevation')}
      </small>
    </Tooltip>
  );

  const Azimuthtooltip = (
    <Tooltip id="tooltip">
      <small>
        {t('verticalPvArea:tooltips.azimuth')}
      </small>
    </Tooltip>
  );

  const tiltedAngle = (
    <Tooltip id="tooltip">
      <small>
        {t('tiltedPvArea:tooltips.tiltAngle')}
        {t('tiltedPvArea:tooltips.tiltAngle2')}
        {t('tiltedPvArea:tooltips.tiltAngle3')}
      </small>
    </Tooltip>
  );

  return (
    <div>
      {(selectedArea?.value == "vertical" ||
        selectedArea?.value == "verticalTab") &&
        polygons.length < 1 && (
          <div className="verticalNothingToShow">
            <div>
              <Image
                src={AddDetectionPoint}
                alt="addTiltedPVarea"
                loading="lazy"
              />
              <strong>{t('verticalPvArea:emptyState.title')}</strong>
              <small>
                {t('verticalPvArea:emptyState.description')}
              </small>
            </div>
          </div>
        )}
      {polygons.length > 0 &&
        (selectedArea?.value == "vertical" ||
          selectedArea?.value == "verticalTab") && (
          <div style={{ height: "76vh", overflowY: "auto" }}>
            {polygons.map((value: any, index: number) => {
              return (
                <div className="VerticalPvAreaPage mb-2" key={`vertical-pv-${index}`}>
                  <Accordion
                    activeKey={isDropdownExpanded[index] ? `${index}` : null}
                  >
                    <Accordion.Item eventKey={`${index}`}>
                      <Accordion.Header onClick={() => toggleDropdown(index)}>
                        <p className="projectName my-auto">
                          {`${t('verticalPvArea:card.prefix')} - ${index + 1}`} &nbsp; |
                          <IconWrapper Icon={MdDeleteOutline} size={17}
                            color="red"
                            className="mb-1"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent accordion toggle
                              handleDeletePolygon(index);
                            }} />
                        </p>
                        <p className={`collapseBtn my-auto`}>
                          {isDropdownExpanded[index] ? (
                            <span>
                              {t('verticalPvArea:card.collapse')} <IconWrapper Icon={RxCaretUp} size={20} />
                            </span>
                          ) : (
                            <span>
                              {t('verticalPvArea:card.expand')} <IconWrapper Icon={RxCaretDown} size={20} />
                            </span>
                          )}
                        </p>
                      </Accordion.Header>
                      <Accordion.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <p className="m-0">
                            {t('verticalPvArea:sections.areaDimensions')}
                          </p>
                        </div>
                        <div className="coolinput">
                          <label htmlFor="input" className="text">
                            {t('verticalPvArea:fields.length')}
                          </label>
                          <input
                            type="text"
                            name="input"
                            className="input disabledInput"
                            value={`${widthValues[index] || 0}`}
                            readOnly
                          />
                        </div>
                        <p className="m-0 mt-4">
                          {t('verticalPvArea:sections.altitudeParameters')}
                        </p>
                        <div className="d-flex justify-content-between mt-1">
                          <div className="coolinput">
                            <label htmlFor="input" className="text">
                              {t('verticalPvArea:fields.upperEdgeHeight')}
                            </label>
                            <input
                              step="0.01"
                              type="text"
                              name="input"
                              className={`input${validationErrors[index]?.upper ? " error-input" : ""}`}
                              onChange={(e) => {
                                const value = oneDigitAfterPoint(e.target.value);
                                const hasError = Number(value) < Number(verticalLowerHeight[index]);
                                
                                // Update validation state
                                setValidationErrors(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    upper: hasError
                                  }
                                }));
                                
                                // Only call handleUpperHeightChange which handles both Redux and Firebase
                                handleUpperHeightChange(index, value === "" ? null : value);
                              }}
                              onBlur={() => handleFieldBlur(index)}
                              value={verticalUpperHeight[index] === null ? "" : verticalUpperHeight[index]}
                            />
                            {validationErrors[index]?.upper && (
                              <div className="validation-error">
                                <IconWrapper Icon={IoInformationCircleOutline} size={14} color="#EA0303"/>
                                <span>{t('verticalPvArea:errors.upperEdgeHeight')}</span>
                              </div>
                            )}
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="coolinput">
                              <label htmlFor="input" className="text">
                                {t('verticalPvArea:fields.lowerEdgeHeight')}
                              </label>
                              <input
                                type="text"
                                step="0.01"
                                name="input"
                                className={`input${validationErrors[index]?.lower ? " error-input" : ""}`}
                                onChange={(e) => {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  const hasError = Number(verticalUpperHeight[index]) < Number(value);
                                  
                                  // Update validation state
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    [index]: {
                                      ...prev[index],
                                      lower: hasError
                                    }
                                  }));
                                  
                                  // Only call handleLowerHeightChange which handles both Redux and Firebase
                                  handleLowerHeightChange(index, value === "" ? null : value);
                                }}
                                onBlur={() => handleFieldBlur(index)}
                                value={verticalLowerHeight[index] === null ? "" : verticalLowerHeight[index]}
                              />
                              {validationErrors[index]?.lower && (
                                <div className="validation-error">
                                  <IconWrapper Icon={IoInformationCircleOutline} size={14} color="#EA0303"/>
                                  <span>{t('verticalPvArea:errors.upperEdgeHeight')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="d-flex gap-1 mt-1">
                            <div className="coolinput">
                              <label htmlFor="input" className="text">
                                {t('verticalPvArea:fields.groundElevation')}
                              </label>
                              <input
                                onChange={(e) => {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  dispatch(
                                    setVeticalAverageElevation({
                                      index,
                                      value: value === "" ? null : value,
                                    })
                                  );
                                }}
                                onBlur={(e) => {
                                  if (!checkedAvgElevation[index]) {
                                    const value = oneDigitAfterPoint(e.target.value);
                                    if (value === "") {
                                      saveManualElevation(index, null);
                                    } else {
                                      saveManualElevation(index, parseFloat(value));
                                    }
                                  }
                                }}
                                type="text"
                                name="input"
                                className={
                                  checkedAvgElevation[index]
                                    ? "disabledInput input"
                                    : "input"
                                }
                                disabled={checkedAvgElevation[index] ? true : false}
                                value={
                                  averageElevation[index] === null
                                    ? ""
                                    : averageElevation[index]
                                }
                              />
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  checked={checkedAvgElevation[index] || false}
                                  onChange={(e) => {
                                    // Use the new handler for elevation auto-calculate
                                    handleElevationAutoCalculateChange(index, e.target.checked);
                                  }}
                                />
                                <label
                                  htmlFor="upperEdge"
                                  className="ms-2"
                                  style={{
                                    fontSize: "12px",
                                    color: "#858585 ",
                                  }}
                                >
                                  {t('verticalPvArea:fields.autoCalculate')}
                                </label>
                              </div>
                            </div>
                            <OverlayTrigger
                              placement="top"
                              overlay={groundElevationTooltip}
                            >
                              <Button variant="none" className="mt-0 p-0">
                                <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                              </Button>
                            </OverlayTrigger>
                          </div>
                          <div
                            className="default"
                            style={{ marginTop: "25px" }}
                          >
                            <div className="d-flex justify-content-between align-items-center px-2 pt-2">
                              <p className="m-0 defaultText">
                                {verticalSwap[index] ? t('verticalPvArea:fields.swapped') : t('verticalPvArea:fields.default')}
                              </p>
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={verticalSwap[index] || false}
                                  onChange={(e) => {
                                    // Use the new handler function for swapping edges
                                    handleSwapEdges(index, value, e.target.checked);
                                  }}
                                />
                                <span className="slider"></span>
                              </label>
                            </div>
                            <strong className="floatingLable">
                              {t('verticalPvArea:fields.swapEdges')}
                            </strong>
                          </div>
                        </div>
                        <p className="m-0 mt-4">
                          {t('verticalPvArea:sections.angleParameters')}
                        </p>
                        <div className="d-flex align-items-center gap-2">
                          <div className="coolinput">
                            <label htmlFor="input" className="text">
                              {t('verticalPvArea:fields.tiltAngle')}
                            </label>
                            <input
                            onChange={(e) => {
                              const value = oneDigitAfterPoint(e.target.value);
                              const numValue = value === "" ? null : parseFloat(value);
                              
                              dispatch(
                                addVerticalSlopeArea({
                                  index,
                                  value: value === "" ? null : value,
                                })
                              );
                              
                              // More explicit conversion to number before saving
                              saveVerticalPVArea(projectId, index, {
                                slope: numValue // Explicitly save as number or null
                              });
                            }}
                              onBlur={() => handleFieldBlur(index)}
                              type="text"
                              name="input"
                              className="input"
                              value={
                                vertivalSlopeArea[index] === null
                                  ? ""
                                  : vertivalSlopeArea[index]
                              }
                            />
                          </div>

                          <OverlayTrigger placement="top" overlay={tiltedAngle}>
                            <Button variant="none" className="mt-3 p-0">
                              <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3" className="mt-3"/>
                            </Button>
                          </OverlayTrigger>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <div className="coolinput">
                            <label htmlFor={`azimuth-input-${index}`} className="text">
                              {t('verticalPvArea:fields.azimuth')}
                            </label>
                            <input
                              id={`azimuth-input-${index}`}
                              onChange={(e) => {
                                // Only update if auto-calculate is OFF
                                if (!checkedAzimuth[index]) {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  dispatch(
                                    setVerticalAzimutyhValue({
                                      index,
                                      azimuth: value === "" ? null : value,
                                    })
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                // Only save manual value if auto-calculate is OFF
                                if (!checkedAzimuth[index] && e.target.value.trim() !== "" && e.target.value !== " ") {
                                  const numValue = parseFloat(oneDigitAfterPoint(e.target.value));
                                  if (!isNaN(numValue)) {
                                    saveManualAzimuth(index, numValue);
                                  }
                                }
                              }}
                              type="text"
                              step="0.1"
                              name="input"
                              className={
                                checkedAzimuth[index]
                                  ? "disabledInput input"
                                  : "input"
                              }
                              disabled={checkedAzimuth[index]}
                              placeholder={
                                calculatingAzimuth[index]
                                  ? "Calculating..."
                                  : checkedAzimuth[index] && (verticalLowerHeight[index] === null || verticalUpperHeight[index] === null)
                                    ? t('verticalPvArea:fields.provideHeightDataFirst')
                                    : ""
                              }
                              value={
                                calculatingAzimuth[index]
                                  ? "" // Use empty string when calculating (placeholder will show "Calculating...")
                                  : verticalAzimuth[index] === null
                                    ? ""
                                    : verticalAzimuth[index]
                              }
                            />
                          </div>
                          <OverlayTrigger
                            placement="top"
                            overlay={Azimuthtooltip}
                          >
                            <Button variant="none" className="mt-3 p-0">
                              <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                            </Button>
                          </OverlayTrigger>
                        </div>

                        <div className="d-flex align-items-center">
                          <Form.Check
                            checked={checkedAzimuth[index] || false}
                            onChange={(e) => {
                              handleAzimuthCheckChange(index, e.target.checked);
                            }}
                            id={`azimuth-auto-calc-${index}`}
                          />
                          <label
                            htmlFor={`azimuth-auto-calc-${index}`}
                            className="ms-2"
                            style={{ fontSize: "12px", color: "#858585" }}
                          >
                            {t('verticalPvArea:fields.autoCalculate')}
                          </label>
                          {/* Manual refresh button - only show when auto-calculate is ON */}
                          {checkedAzimuth[index] && (
                            <Button 
                              variant="link" 
                              className="p-0 ms-2" 
                              onClick={() => forceAzimuthCalculation(index)}
                              disabled={calculatingAzimuth[index]} // Disable during calculation
                              title={t('verticalPvArea:sections.recalculateAzimuth')}
                            >
                            </Button>
                          )}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default VerticalPvAreaPage;